/**
 * AufgabenDetail — die eine Detailansicht für Aufgaben (Strato-Stil).
 *
 * Wird überall verwendet: im Aufgabenfenster (TaskForm, global über die Hauptnavigation,
 * Adressen, Projekte, Kontakthistorie …) und eingebettet als rechte Spalte der Aufgabenseite.
 *
 * Modi:
 *   Ansicht     Symbolleiste · Pfad · Titel · Feldraster · Unteraufgaben-Baum · KI-Empfehlung
 *   Bearbeiten  gleiche Anordnung, Werte werden zu Eingabefeldern (kein zweites Modal);
 *               selten genutzte Angaben liegen in aufklappbaren Abschnitten
 *   Neu         wie Bearbeiten, ohne Aufgabe
 *
 * Props:
 *   taskId | task                vorhandene Aufgabe (task darf unvollständig sein, wird nachgeladen)
 *   preselectedAddressId, preselectedProjectId, initialStatus, defaultTitle, defaultDescription  (Neuanlage)
 *   parentTask                   Neuanlage als Unteraufgabe
 *   eingebettet                  true = rechte Spalte der Aufgabenseite (kein Fensterrahmen)
 *   onGespeichert(task, {neu})   nach Anlegen/Speichern/Statuswechsel
 *   onGeloescht(task)
 *   onSchliessen()               „Abbrechen“ bei Neuanlage bzw. nach dem Anlegen im Fenster
 *   onAufgabeOeffnen(task)       Unteraufgabe/Hauptaufgabe öffnen (Standard: im selben Fenster)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Pencil, Clock, CheckCircle2, CalendarPlus, Trash2, ChevronDown, FileDown, ListPlus, Loader2, X, Upload, FileText,
  Sparkles, Lightbulb, RefreshCw, Copy, Globe, Send, BookmarkPlus, RotateCcw, Repeat,
} from 'lucide-react';
import { toast } from 'sonner';
import taskService from '../../services/taskService';
import addressService from '../../services/addressService';
import projectService from '../../services/projectService';
import aiPromptService from '../../services/aiPromptService';
import userService from '../../services/userService';
import documentService from '../../services/documentService';
import settingsService from '../../services/settingsService';
import DropdownCombobox from '../ui/DropdownCombobox';
import DictationButton from '../DictationButton';
import CustomFieldsEditor from '../CustomFieldsEditor';
import AccessRightsButton from '../access/AccessRightsButton';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import { useTerminFenster } from '../../contexts/TerminFensterContext';
import {
  DetailWerkzeugleiste, DetailBrotkrumen, DetailTitel, FeldRaster, Feld, LeerWert, StatusEtikett,
  Fortschrittsbalken, DetailAbschnitt, EINGABE_KLASSE, SuchAuswahl, GewaehlterEintrag,
} from '../detailfenster/DetailFenster';
import AufgabenBaum from './AufgabenBaum';
import {
  AUFGABEN_STATUS, AUFGABEN_PRIORITAET, ERINNERUNG_OPTIONEN, WIEDERHOLUNG_LABEL,
  statusInfo, prioritaetInfo, deDatum, istErledigt, istUeberfaellig, adressName, benutzerName, initialen,
  baueBaum, blaetter,
} from './aufgabenKonstanten';

const LEERES_FORMULAR = {
  title: '', description: '', notes: '', role: '', status: 'open', priority: 'medium',
  address_id: '', project_id: '', assigned_to_id: '', due_date: '', due_time: '', start_date: '',
  reminder_enabled: true, reminder_time: '1d', estimated_hours: '', task_type: '',
  ai_refined_description: '', ai_solution_recommendation: '', ai_prompt: '',
  send_to_ai_assistant: false, save_to_prompt_collection: false, document_ids: [], business_division: '',
  is_recurring: false, recurrence_pattern: 'weekly', recurrence_interval: 1, recurrence_days_of_week: [],
  contact_method: '', contact_template_id: '', custom_fields: {},
};

const AUFGABENTYPEN = ['Anruf', 'Dokument', 'E-Mail', 'Meeting', 'Recherche', 'Sonstiges'];
const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const formularAus = (t) => ({
  ...LEERES_FORMULAR,
  title: t.title || '',
  description: t.description || '',
  notes: t.notes || '',
  role: t.role || '',
  status: t.status || 'open',
  priority: t.priority || 'medium',
  address_id: t.address_id || '',
  project_id: t.project_id || '',
  assigned_to_id: t.assigned_to_id || '',
  due_date: t.due_date ? String(t.due_date).split('T')[0] : '',
  due_time: t.due_time || '',
  start_date: t.start_date ? String(t.start_date).split('T')[0] : '',
  reminder_enabled: !!t.reminder_enabled,
  reminder_time: t.reminder_time || '1h',
  estimated_hours: t.estimated_hours || '',
  task_type: t.task_type || '',
  ai_refined_description: t.ai_refined_description || '',
  ai_solution_recommendation: t.ai_solution_recommendation || '',
  ai_prompt: t.ai_prompt || '',
  document_ids: t.document_ids || [],
  business_division: t.business_division || '',
  is_recurring: !!t.is_recurring,
  recurrence_pattern: t.recurrence_pattern || 'weekly',
  recurrence_interval: t.recurrence_interval || 1,
  recurrence_days_of_week: Array.isArray(t.recurrence_days_of_week) ? t.recurrence_days_of_week : [],
  contact_method: t.contact_method || '',
  contact_template_id: t.contact_template_id || '',
  custom_fields: t.custom_fields || {},
});

/** Lädt alle Unteraufgaben (beliebig tief) als flache Liste */
async function ladeUnteraufgabenTief(wurzelId, maxTiefe = 6) {
  const alle = [];
  let ebene = [wurzelId];
  for (let tiefe = 0; tiefe < maxTiefe && ebene.length; tiefe += 1) {
    const ergebnisse = await Promise.all(ebene.map((id) => taskService.getSubtasks(id).catch(() => [])));
    const naechste = [];
    ergebnisse.flat().forEach((t) => {
      if (t?.id && !t.deleted_at && !alle.some((x) => x.id === t.id)) { alle.push(t); naechste.push(t.id); }
    });
    ebene = naechste;
  }
  return alle;
}

export default function AufgabenDetail({
  taskId = null, task: aufgabeProp = null,
  preselectedAddressId = null, preselectedProjectId = null, initialStatus = 'open',
  defaultTitle = '', defaultDescription = '', parentTask = null,
  eingebettet = false, onGespeichert, onGeloescht, onSchliessen, onAufgabeOeffnen,
}) {
  const { user: authUser } = useAuth();
  const { oeffnen: terminFensterOeffnen } = useTerminFenster();
  const istStudent = !!authUser?.is_student;
  const aktuelleId = taskId || aufgabeProp?.id || null;

  // ── Aufgabe & Modus ──
  const vollstaendig = (t) => !!(t?.id && t.title);
  const [aufgabe, setAufgabe] = useState(vollstaendig(aufgabeProp) ? aufgabeProp : null);
  const [laedt, setLaedt] = useState(!!aktuelleId);
  const [modus, setModus] = useState(aktuelleId ? 'ansicht' : 'bearbeiten');
  const [formData, setFormData] = useState(LEERES_FORMULAR);
  const [speichert, setSpeichert] = useState(false);
  const [loescht, setLoescht] = useState(false);
  const [gespeicherteRechte, setGespeicherteRechte] = useState(null);

  // ── Umfeld ──
  const [eltern, setEltern] = useState([]); // Pfad von der obersten Hauptaufgabe bis zur direkten Elternaufgabe
  const [unteraufgaben, setUnteraufgaben] = useState([]);
  const [neueUnteraufgabe, setNeueUnteraufgabe] = useState(null); // null = geschlossen, sonst Eingabetext
  const [erinnerungOffen, setErinnerungOffen] = useState(false);

  // ── Auswahllisten ──
  const [users, setUsers] = useState([]);
  const [businessDivisions, setBusinessDivisions] = useState([]);
  const [contactMethods, setContactMethods] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [promptCategories, setPromptCategories] = useState([]);
  const [selectedPromptCategory, setSelectedPromptCategory] = useState('');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [addressSearch, setAddressSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentSearch, setDocumentSearch] = useState('');
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // ── KI ──
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [includeWebSearch, setIncludeWebSearch] = useState(false);

  const bearbeiten = modus === 'bearbeiten';
  const istNeu = !aufgabe;
  const elternFuerNeu = istNeu ? parentTask : null;

  // ── Laden ──
  const ladeAdresse = useCallback(async (id) => {
    if (!id) { setSelectedAddress(null); return; }
    try { setSelectedAddress(await addressService.getById(id)); } catch { setSelectedAddress(null); }
  }, []);
  const ladeProjekt = useCallback(async (id) => {
    if (!id) { setSelectedProject(null); return; }
    try { setSelectedProject(await projectService.getById(id)); } catch { setSelectedProject(null); }
  }, []);

  const ladeUmfeld = useCallback(async (t) => {
    // Pfad der Elternaufgaben
    const kette = [];
    let pid = t.parent_task_id;
    for (let i = 0; pid && i < 6; i += 1) {
      try {
        const p = await taskService.getById(pid);
        kette.unshift(p);
        pid = p.parent_task_id;
      } catch { break; }
    }
    setEltern(kette);
    setUnteraufgaben(await ladeUnteraufgabenTief(t.id));
  }, []);

  const ladeAufgabe = useCallback(async (id) => {
    setLaedt(true);
    try {
      const t = await taskService.getById(id);
      setAufgabe(t);
      setFormData(formularAus(t));
      setSelectedDocuments(t.document_ids || []);
      setAiResult(null);
      ladeAdresse(t.address_id);
      ladeProjekt(t.project_id);
      ladeUmfeld(t);
      return t;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aufgabe konnte nicht geladen werden');
      return null;
    } finally {
      setLaedt(false);
    }
  }, [ladeAdresse, ladeProjekt, ladeUmfeld]);

  // Auswahllisten einmalig
  useEffect(() => {
    userService.getUsers({ is_active: true }).then((d) => setUsers(d?.users || [])).catch(() => setUsers([]));
    settingsService.getBusinessDivisions().then((d) => setBusinessDivisions(d || [])).catch(() => setBusinessDivisions([]));
    settingsService.getDropdownValues('contact_methods')
      .then((d) => setContactMethods(Array.isArray(d?.values) ? d.values : []))
      .catch(() => setContactMethods([]));
    import('../../services/templateService').then(({ default: templateService }) => {
      templateService.getEmailTemplates(null, true)
        .then((d) => setEmailTemplates(Array.isArray(d?.templates) ? d.templates : []))
        .catch(() => setEmailTemplates([]));
    });
    aiPromptService.getCategories().then((c) => setPromptCategories(c || [])).catch(() => setPromptCategories([]));
  }, []);

  // Aufgabe wechseln / Neuanlage vorbereiten
  useEffect(() => {
    setErinnerungOffen(false);
    setNeueUnteraufgabe(null);
    if (aktuelleId) {
      if (aufgabeProp?.id === aktuelleId && vollstaendig(aufgabeProp)) {
        setAufgabe(aufgabeProp);
        setFormData(formularAus(aufgabeProp));
      }
      else setAufgabe(null);
      setModus('ansicht');
      ladeAufgabe(aktuelleId);
    } else {
      setAufgabe(null);
      setEltern(parentTask ? [parentTask] : []);
      setUnteraufgaben([]);
      setModus('bearbeiten');
      setAiResult(null);
      setSelectedDocuments([]);
      const vorbelegung = {
        ...LEERES_FORMULAR,
        status: initialStatus || 'open',
        title: defaultTitle || '',
        description: defaultDescription || '',
        assigned_to_id: authUser?.id || '',
        address_id: preselectedAddressId || parentTask?.address_id || '',
        project_id: preselectedProjectId || parentTask?.project_id || '',
      };
      setFormData(vorbelegung);
      ladeAdresse(vorbelegung.address_id);
      ladeProjekt(vorbelegung.project_id);
      setLaedt(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktuelleId, preselectedAddressId, preselectedProjectId, parentTask?.id, defaultTitle, defaultDescription, initialStatus]);

  // Suche Adressen (serverseitig, verzögert) und Projekte
  useEffect(() => {
    if (!bearbeiten) return undefined;
    const t = setTimeout(async () => {
      setLoadingAddresses(true);
      try {
        const params = { limit: 50 };
        if (addressSearch.trim()) params.search = addressSearch.trim();
        const d = await addressService.getAll(params);
        setAddresses(Array.isArray(d) ? d : (d?.addresses || []));
      } catch { /* ignorieren */ } finally { setLoadingAddresses(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [addressSearch, bearbeiten]);

  useEffect(() => {
    if (!bearbeiten || projects.length) return;
    setLoadingProjects(true);
    projectService.getAll({ limit: 100 })
      .then((d) => setProjects(Array.isArray(d) ? d : (d?.projects || d?.items || [])))
      .catch(() => {})
      .finally(() => setLoadingProjects(false));
  }, [bearbeiten, projects.length]);

  // Dokumente zur gewählten Adresse / zum Projekt
  const ladeDokumente = useCallback(async () => {
    setLoadingDocuments(true);
    try {
      const params = { limit: 100 };
      if (formData.address_id) params.address_id = formData.address_id;
      if (formData.project_id) params.project_id = formData.project_id;
      const d = await documentService.getAll(params);
      const liste = Array.isArray(d) ? d : (d?.documents || d?.items || []);
      setDocuments(Array.isArray(liste) ? liste : []);
    } catch { /* ignorieren */ } finally { setLoadingDocuments(false); }
  }, [formData.address_id, formData.project_id]);

  useEffect(() => { ladeDokumente(); }, [ladeDokumente]);

  // ── Helfer ──
  const setFeld = (name, wert) => setFormData((alt) => ({ ...alt, [name]: wert }));
  const handleChange = (e) => setFeld(e.target.name, e.target.value);

  const benutzerVon = (id) => users.find((u) => u.id === id);
  const zustaendigName = aufgabe?.assigned_to_name || benutzerName(benutzerVon(aufgabe?.assigned_to_id));

  const baum = useMemo(() => baueBaum(unteraufgaben), [unteraufgaben]);
  const unterBlaetter = useMemo(() => baum.flatMap(blaetter), [baum]);
  const fortschrittProzent = unterBlaetter.length
    ? Math.round((unterBlaetter.filter(istErledigt).length / unterBlaetter.length) * 100)
    : (aufgabe && istErledigt(aufgabe) ? 100 : (aufgabe?.progress_percentage || 0));

  const oeffne = (t) => {
    if (onAufgabeOeffnen) onAufgabeOeffnen(t);
    else ladeAufgabe(t.id);
  };

  // ── Aktionen ──
  const statusSetzen = async (t, neuerStatus) => {
    try {
      await taskService.updateStatus(t.id, neuerStatus);
      toast.success(neuerStatus === 'completed' ? 'Als erledigt markiert' : neuerStatus === 'open' ? 'Wieder geöffnet' : 'Status aktualisiert');
      if (t.id === aufgabe?.id) {
        const neu = await ladeAufgabe(t.id);
        onGespeichert?.(neu || { ...t, status: neuerStatus }, { neu: false });
      } else {
        setUnteraufgaben(await ladeUnteraufgabenTief(aufgabe.id));
        onGespeichert?.(aufgabe, { neu: false });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status konnte nicht geändert werden');
    }
  };

  const erinnerungSpeichern = async (felder) => {
    setFormData((alt) => ({ ...alt, ...felder }));
    if (!aufgabe) return;
    try {
      const daten = { ...felder };
      if ('reminder_enabled' in daten && !daten.reminder_enabled) daten.reminder_time = null;
      await taskService.update(aufgabe.id, daten);
      setAufgabe((alt) => ({ ...alt, ...daten }));
      toast.success('Erinnerung gespeichert');
      onGespeichert?.({ ...aufgabe, ...daten }, { neu: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erinnerung konnte nicht gespeichert werden');
    }
  };

  const loeschen = async () => {
    if (!aufgabe) return;
    const hinweis = unteraufgaben.length ? ` Sie hat ${unteraufgaben.length} Unteraufgabe(n).` : '';
    if (!window.confirm(`Aufgabe „${aufgabe.title}“ wirklich löschen?${hinweis}`)) return;
    setLoescht(true);
    try {
      await taskService.delete(aufgabe.id);
      toast.success('Aufgabe gelöscht');
      onGeloescht?.(aufgabe);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setLoescht(false);
    }
  };

  const pdfExport = async () => {
    if (!aufgabe) return;
    try {
      const blob = await taskService.exportSinglePdf(aufgabe.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aufgabe-${(aufgabe.title || 'export').replace(/[^\wäöüÄÖÜß-]+/g, '-').slice(0, 40)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  const unteraufgabeAnlegen = async () => {
    const titel = (neueUnteraufgabe || '').trim();
    if (!titel || !aufgabe) return;
    try {
      await taskService.create({
        title: titel,
        status: 'open',
        priority: 'medium',
        parent_task_id: aufgabe.id,
        address_id: aufgabe.address_id || null,
        project_id: aufgabe.project_id || null,
        assigned_to_id: aufgabe.assigned_to_id || authUser?.id || null,
        business_division: aufgabe.business_division || null,
        reminder_enabled: false,
      });
      setNeueUnteraufgabe('');
      setUnteraufgaben(await ladeUnteraufgabenTief(aufgabe.id));
      // Hauptaufgabe ist mit neuer offener Unteraufgabe nicht mehr „abgeschlossen“
      if (istErledigt(aufgabe)) await taskService.updateStatus(aufgabe.id, 'in_progress').catch(() => {});
      onGespeichert?.(aufgabe, { neu: false });
      toast.success('Unteraufgabe angelegt');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unteraufgabe konnte nicht angelegt werden');
    }
  };

  // ── Dokumente ──
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingFile(true);
    try {
      for (const file of files) {
        const result = await documentService.upload({
          file,
          address_id: formData.address_id || null,
          project_id: formData.project_id || null,
          folder_path: 'Aufgaben',
          description: `Dokument für Aufgabe: ${formData.title || 'Neue Aufgabe'}`,
        });
        setSelectedDocuments((alt) => [...alt, result.id]);
        toast.success(`${file.name} hochgeladen`);
      }
      ladeDokumente();
    } catch {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  // ── KI ──
  const handleAiAssist = async () => {
    if (!formData.title.trim()) { toast.error('Bitte zuerst einen Aufgabentitel eingeben'); return; }
    setAiLoading(true);
    try {
      let context = '';
      if (selectedAddress) context += `Kunde/Adresse: ${adressName(selectedAddress)}. `;
      if (selectedProject) context += `Projekt: ${selectedProject.name}. `;
      const result = await taskService.aiAssist({
        title: formData.title, description: formData.description, task_type: formData.task_type,
        context: context || undefined, include_web_search: includeWebSearch,
      });
      setAiResult(result);
      setFormData((alt) => ({
        ...alt,
        ai_refined_description: result.refined_description,
        ai_solution_recommendation: result.solution_recommendation,
        priority: alt.priority === 'medium' && result.suggested_priority ? result.suggested_priority : alt.priority,
        estimated_hours: !alt.estimated_hours && result.estimated_hours ? result.estimated_hours : alt.estimated_hours,
      }));
      toast.success('KI-Analyse abgeschlossen');
    } catch {
      toast.error('Fehler bei der KI-Analyse');
    } finally {
      setAiLoading(false);
    }
  };

  const generateAiPrompt = () => {
    setFeld('ai_prompt', `Aufgabe: ${formData.title}
Rolle: ${formData.role || 'Nicht angegeben'}
Beschreibung: ${formData.description || 'Keine Beschreibung'}

Bitte unterstütze mich bei dieser Aufgabe und gib konkrete Handlungsempfehlungen.`);
    toast.success('KI-Prompt generiert');
  };

  const savePromptToCollection = async () => {
    try {
      await aiPromptService.create({
        title: formData.title, role: formData.role || null, description: formData.description || null,
        prompt_text: formData.ai_prompt, category_id: selectedPromptCategory || null,
        tags: [formData.task_type].filter(Boolean),
      });
      toast.success('Prompt in Sammlung gespeichert');
    } catch {
      toast.error('Fehler beim Speichern des Prompts');
    }
  };

  // ── Speichern ──
  const speichern = async (e) => {
    e?.preventDefault?.();
    if (!formData.title.trim()) { toast.error('Bitte Aufgabentitel eingeben'); return; }
    setSpeichert(true);
    try {
      const daten = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        due_date: formData.due_date || null,
        due_time: formData.due_time || null,
        start_date: formData.start_date || null,
        reminder_time: formData.reminder_enabled ? formData.reminder_time : null,
        address_id: formData.address_id || null,
        project_id: formData.project_id || null,
        assigned_to_id: formData.assigned_to_id || null,
        ai_refined_description: formData.ai_refined_description || null,
        ai_solution_recommendation: formData.ai_solution_recommendation || null,
        ai_prompt: formData.ai_prompt || null,
        role: formData.role || null,
        notes: formData.notes || null,
        document_ids: selectedDocuments,
        business_division: formData.business_division || null,
        parent_task_id: aufgabe ? (aufgabe.parent_task_id || null) : (parentTask?.id || null),
        is_recurring: !!formData.is_recurring,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null,
        recurrence_interval: formData.is_recurring ? Math.max(1, parseInt(formData.recurrence_interval, 10) || 1) : 1,
        recurrence_days_of_week: (formData.is_recurring && formData.recurrence_pattern === 'weekly') ? (formData.recurrence_days_of_week || []) : [],
        contact_method: formData.contact_method || null,
        contact_template_id: formData.contact_template_id || null,
        custom_fields: formData.custom_fields || {},
      };
      delete daten.send_to_ai_assistant;
      delete daten.save_to_prompt_collection;

      const war = aufgabe;
      const gespeichert = war ? await taskService.update(war.id, daten) : await taskService.create(daten);
      toast.success(war ? 'Aufgabe gespeichert' : (parentTask ? 'Unteraufgabe angelegt' : 'Aufgabe angelegt'));

      if (formData.save_to_prompt_collection && formData.ai_prompt) await savePromptToCollection();

      if (war) {
        await ladeAufgabe(war.id);
        setModus('ansicht');
      }
      onGespeichert?.(gespeichert, { neu: !war });

      if (formData.send_to_ai_assistant && formData.ai_prompt) {
        setTimeout(() => { window.location.href = `/ai-assistant?prompt=${encodeURIComponent(formData.ai_prompt)}`; }, 100);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSpeichert(false);
    }
  };

  const abbrechen = () => {
    if (aufgabe) {
      setFormData(formularAus(aufgabe));
      setSelectedDocuments(aufgabe.document_ids || []);
      ladeAdresse(aufgabe.address_id);
      ladeProjekt(aufgabe.project_id);
      setModus('ansicht');
    } else {
      onSchliessen?.();
    }
  };

  if (laedt && !aufgabe) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400" data-testid="aufgaben-detail-laedt">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const st = statusInfo(aufgabe?.status || formData.status);
  const prio = prioritaetInfo(aufgabe?.priority || formData.priority);
  const erledigt = aufgabe ? istErledigt(aufgabe) : false;
  const ueberfaellig = aufgabe ? istUeberfaellig(aufgabe) : false;

  // ── Pfad ──
  const brotkrumen = [
    selectedProject && !bearbeiten ? { key: 'p', label: selectedProject.name, onClick: undefined } : null,
    ...eltern.map((p) => ({ key: p.id, label: p.title, onClick: () => oeffne(p) })),
  ];
  if (istNeu) brotkrumen.push({ key: 'neu', label: parentTask ? 'Neue Unteraufgabe' : 'Neue Aufgabe' });

  // ── Symbolleiste ──
  const werkzeuge = istNeu ? [] : [
    { key: 'bearbeiten', label: bearbeiten ? 'Bearbeiten beenden' : 'Bearbeiten', icon: Pencil, aktiv: bearbeiten, onClick: () => (bearbeiten ? abbrechen() : setModus('bearbeiten')) },
    {
      key: 'erinnerung', label: 'Erinnerung', icon: Clock, aktiv: erinnerungOffen,
      inhalt: (
        <span className="flex items-center gap-0.5">
          <Clock className={`w-[18px] h-[18px] ${aufgabe?.reminder_enabled ? '' : 'opacity-60'}`} strokeWidth={1.8} />
          <ChevronDown className="w-3 h-3" />
        </span>
      ),
      onClick: () => setErinnerungOffen((o) => !o),
    },
    {
      key: 'erledigt', label: erledigt ? 'Wieder öffnen' : 'Als erledigt markieren', icon: erledigt ? RotateCcw : CheckCircle2,
      onClick: () => statusSetzen(aufgabe, erledigt ? 'open' : 'completed'),
    },
    { key: 'loeschen', label: 'Löschen', icon: loescht ? Loader2 : Trash2, gefahr: true, disabled: loescht, onClick: loeschen },
  ];
  const mehr = istNeu ? [] : [
    !erledigt && { key: 'unteraufgabe', label: 'Unteraufgabe hinzufügen', icon: ListPlus, onClick: () => setNeueUnteraufgabe('') },
    {
      key: 'termin', label: 'Termin anlegen', icon: CalendarPlus,
      onClick: () => terminFensterOeffnen({
        titel: aufgabe?.title || '',
        beschreibung: `Bezug: Aufgabe „${aufgabe?.title || ''}“`,
        addressId: aufgabe?.address_id || null,
        projectId: aufgabe?.project_id || null,
      }),
    },
    { key: 'pdf', label: 'Als PDF exportieren', icon: FileDown, onClick: pdfExport },
    ...AUFGABEN_STATUS.filter((s) => s.wert !== aufgabe?.status).map((s) => ({
      key: `status-${s.wert}`, label: `Status: ${s.label}`, onClick: () => statusSetzen(aufgabe, s.wert),
    })),
  ];
  const rechteKnopf = aufgabe?.id ? (
    <AccessRightsButton
      entity="tasks"
      record={{ ...aufgabe, access_rights: gespeicherteRechte?.id === aufgabe.id ? gespeicherteRechte.value : aufgabe.access_rights }}
      title={aufgabe.title}
      ownerIds={[aufgabe.created_by, aufgabe.created_by_id].filter(Boolean)}
      onSaved={(value) => setGespeicherteRechte({ id: aufgabe.id, value })}
      className="h-8 w-8"
    />
  ) : null;

  const filteredAddresses = addresses;
  const filteredProjects = projects.filter((p) => {
    const s = projectSearch.toLowerCase();
    return !s || (p.name || '').toLowerCase().includes(s) || (p.project_number || '').toLowerCase().includes(s);
  });
  const filteredDocuments = documents
    .filter((d) => !documentSearch || d.filename?.toLowerCase().includes(documentSearch.toLowerCase()) || d.description?.toLowerCase().includes(documentSearch.toLowerCase()))
    .filter((d) => !selectedDocuments.includes(d.id));
  const dokumentName = (id) => documents.find((d) => d.id === id)?.filename || 'Dokument';

  // ════════════════════════ Darstellung ════════════════════════
  return (
    <form
      onSubmit={speichern}
      className={`flex flex-col h-full bg-white ${eingebettet ? '' : ''}`}
      data-testid="aufgaben-detail"
    >
      <div className={`flex-1 overflow-y-auto ${eingebettet ? 'px-6 py-4' : 'px-6 py-4'}`}>
        {!istNeu && <DetailWerkzeugleiste aktionen={werkzeuge} mehr={mehr} rechts={rechteKnopf} />}

        {erinnerungOffen && aufgabe && (
          <div className="mt-2 max-w-[420px] border border-gray-200 rounded-md p-3 bg-gray-50 flex flex-col gap-2" data-testid="aufgaben-erinnerung-panel">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-[#1E3A5F]">Erinnerung aktiv</span>
              <Switch checked={!!formData.reminder_enabled} onCheckedChange={(v) => erinnerungSpeichern({ reminder_enabled: v, reminder_time: formData.reminder_time || '1d' })} data-testid="aufgaben-erinnerung-switch" />
            </label>
            {formData.reminder_enabled && (
              <select
                value={formData.reminder_time || '1d'}
                onChange={(e) => erinnerungSpeichern({ reminder_enabled: true, reminder_time: e.target.value })}
                className={EINGABE_KLASSE}
                data-testid="aufgaben-erinnerung-zeit"
              >
                {ERINNERUNG_OPTIONEN.map((o) => <option key={o.wert} value={o.wert}>{o.label}</option>)}
              </select>
            )}
            {!aufgabe.due_date && <p className="text-xs text-gray-500">Für eine Erinnerung braucht die Aufgabe ein Fälligkeitsdatum.</p>}
          </div>
        )}

        <div className={istNeu ? 'mt-1' : 'mt-6'}>
          <DetailBrotkrumen teile={brotkrumen} />
        </div>
        <div className="mt-1.5 mb-6">
          <DetailTitel
            wert={bearbeiten ? formData.title : aufgabe?.title}
            bearbeiten={bearbeiten}
            durchgestrichen={erledigt}
            eingabe={(
              <DropdownCombobox
                value={formData.title}
                onChange={(v) => setFeld('title', v)}
                category="task_title"
                data-testid="task-title-input"
                placeholder={parentTask ? 'Titel der Unteraufgabe' : 'Titel der Aufgabe, z. B. Angebot erstellen'}
                className="w-full text-2xl font-bold text-[#1E3A5F] bg-transparent border-0 border-b-2 border-[#2B2FB5]/40 focus:border-[#2B2FB5] focus:outline-none px-0 py-1"
              />
            )}
          />
        </div>

        {/* ── Feldraster ── */}
        <FeldRaster>
          <Feld label="Status" testid="aufgaben-feld-status">
            {bearbeiten ? (
              <select name="status" value={formData.status} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-status-select">
                {AUFGABEN_STATUS.map((s) => <option key={s.wert} value={s.wert}>{s.label}</option>)}
              </select>
            ) : <StatusEtikett klasse={st.klasse}>{st.label}</StatusEtikett>}
          </Feld>

          <Feld label="Priorität">
            {bearbeiten ? (
              <select name="priority" value={formData.priority} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-priority-select">
                {AUFGABEN_PRIORITAET.map((p) => <option key={p.wert} value={p.wert}>{p.label}</option>)}
              </select>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: prio.farbe }} />{prio.label}
              </span>
            )}
          </Feld>

          <Feld label="Fällig am">
            {bearbeiten ? (
              <span className="flex flex-wrap items-center gap-2">
                <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-due-date-input" />
                <input type="time" name="due_time" value={formData.due_time} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-due-time-input" />
                <span className="text-xs text-gray-500">Uhrzeit optional</span>
              </span>
            ) : aufgabe?.due_date ? (
              <span className={ueberfaellig ? 'text-red-600 font-medium' : ''}>
                {deDatum(aufgabe.due_date)}{aufgabe.due_time ? `, ${aufgabe.due_time} Uhr` : ''}{ueberfaellig ? ' · überfällig' : ''}
              </span>
            ) : <LeerWert />}
          </Feld>

          {(bearbeiten || aufgabe?.start_date) && (
            <Feld label="Beginnt am">
              {bearbeiten
                ? <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-start-date-input" />
                : deDatum(aufgabe.start_date)}
            </Feld>
          )}

          {!istStudent && (
            <Feld label="Zuständig">
              {bearbeiten ? (
                <select name="assigned_to_id" value={formData.assigned_to_id} onChange={handleChange} className={`${EINGABE_KLASSE} max-w-[380px] w-full`} data-testid="task-assigned-to-select-top">
                  <option value="">Nicht zugewiesen</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{benutzerName(u)}</option>)}
                </select>
              ) : zustaendigName ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#ececf3] grid place-items-center text-[10.5px] font-semibold text-[#1E3A5F]">{initialen(zustaendigName)}</span>
                  {zustaendigName}
                </span>
              ) : <LeerWert text="Nicht zugewiesen" />}
            </Feld>
          )}

          <Feld label="Adresse">
            {selectedAddress ? (
              <GewaehlterEintrag
                text={adressName(selectedAddress)}
                unter={bearbeiten ? selectedAddress.email : null}
                href={bearbeiten ? null : `/addresses/${selectedAddress.id}`}
                onEntfernen={bearbeiten ? () => { setSelectedAddress(null); setFeld('address_id', ''); } : null}
              />
            ) : bearbeiten ? (
              <SuchAuswahl
                platzhalter="Adresse suchen …" suche={addressSearch} setSuche={setAddressSearch} laedt={loadingAddresses}
                eintraege={filteredAddresses} beschriftung={adressName} unterzeile={(a) => a.email} testid="task-address-search"
                onWaehle={(a) => { setSelectedAddress(a); setFeld('address_id', a.id); setAddressSearch(''); }}
              />
            ) : <LeerWert />}
          </Feld>

          <Feld label="Projekt">
            {selectedProject ? (
              <GewaehlterEintrag
                text={selectedProject.name}
                unter={selectedProject.project_number}
                href={bearbeiten ? null : `/projects/${selectedProject.id}`}
                onEntfernen={bearbeiten ? () => { setSelectedProject(null); setFeld('project_id', ''); } : null}
              />
            ) : bearbeiten ? (
              <SuchAuswahl
                platzhalter="Projekt suchen …" suche={projectSearch} setSuche={setProjectSearch} laedt={loadingProjects}
                eintraege={filteredProjects} beschriftung={(p) => p.name} unterzeile={(p) => p.project_number} testid="task-project-search"
                onWaehle={(p) => {
                  setSelectedProject(p); setFeld('project_id', p.id); setProjectSearch('');
                  if (p.address_id && !formData.address_id) { setFeld('address_id', p.address_id); ladeAdresse(p.address_id); }
                }}
              />
            ) : <LeerWert />}
          </Feld>

          {!istNeu && (
            <Feld label="Fortschritt">
              <span className="tabular-nums">{fortschrittProzent} %</span>
              {unterBlaetter.length > 0 && <span className="text-xs text-gray-500"> · aus {unterBlaetter.length} Unteraufgaben berechnet</span>}
              <Fortschrittsbalken prozent={fortschrittProzent} />
            </Feld>
          )}

          <Feld label="Beschreibung" oben={bearbeiten}>
            {bearbeiten ? (
              <div className="flex items-start gap-1">
                <textarea name="description" value={formData.description} onChange={handleChange} rows={4} placeholder="Details zur Aufgabe …" className={`${EINGABE_KLASSE} w-full`} data-testid="task-description-input" />
                <DictationButton testId="task-description-dictation" size="sm" onResult={(text) => setFormData((alt) => ({ ...alt, description: (alt.description ? `${alt.description}\n` : '') + text }))} />
              </div>
            ) : aufgabe?.description ? <p className="whitespace-pre-line break-words">{aufgabe.description}</p> : <LeerWert text="Keine Beschreibung" />}
          </Feld>

          {(bearbeiten || aufgabe?.notes) && (
            <Feld label="Notizen" oben={bearbeiten}>
              {bearbeiten ? (
                <div className="flex items-start gap-1">
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} placeholder="Interne Notizen …" className={`${EINGABE_KLASSE} w-full bg-amber-50/50 border-amber-200`} data-testid="task-notes-input" />
                  <DictationButton testId="task-notes-dictation" size="sm" onResult={(text) => setFormData((alt) => ({ ...alt, notes: (alt.notes ? `${alt.notes}\n` : '') + text }))} />
                </div>
              ) : <p className="whitespace-pre-line break-words">{aufgabe.notes}</p>}
            </Feld>
          )}

          {!bearbeiten && aufgabe?.reminder_enabled && (
            <Feld label="Erinnerung">{ERINNERUNG_OPTIONEN.find((o) => o.wert === aufgabe.reminder_time)?.label || 'Aktiv'}</Feld>
          )}
          {!bearbeiten && aufgabe?.is_recurring && (
            <Feld label="Wiederholung">
              <span className="inline-flex items-center gap-1.5">
                <Repeat className="w-3.5 h-3.5 text-violet-600" />
                {WIEDERHOLUNG_LABEL[aufgabe.recurrence_pattern] || 'Wiederkehrend'}
                {aufgabe.recurrence_interval > 1 ? ` (alle ${aufgabe.recurrence_interval})` : ''}
                {aufgabe.recurrence_pattern === 'weekly' && aufgabe.recurrence_days_of_week?.length > 0
                  ? ` · ${aufgabe.recurrence_days_of_week.map((d) => WOCHENTAGE[d]).join(', ')}` : ''}
              </span>
            </Feld>
          )}
          {!bearbeiten && aufgabe?.task_type && <Feld label="Aufgabentyp">{aufgabe.task_type}</Feld>}
          {!bearbeiten && aufgabe?.business_division && !istStudent && (
            <Feld label="Unternehmensbereich">{businessDivisions.find((d) => d.value === aufgabe.business_division)?.label || aufgabe.business_division}</Feld>
          )}
          {!bearbeiten && aufgabe?.estimated_hours > 0 && <Feld label="Geschätzt">{String(aufgabe.estimated_hours).replace('.', ',')} Std.</Feld>}
          {!bearbeiten && aufgabe?.contact_method && <Feld label="Kontaktaufnahme">{aufgabe.contact_method}</Feld>}
          {!bearbeiten && selectedDocuments.length > 0 && (
            <Feld label="Dokumente">
              <span className="flex flex-col gap-0.5">
                {selectedDocuments.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-gray-400" />{dokumentName(id)}</span>
                ))}
              </span>
            </Feld>
          )}
        </FeldRaster>

        {/* ── Weitere Angaben (nur Bearbeiten) ── */}
        {bearbeiten && (
          <div className="mt-8 space-y-6">
            <DetailAbschnitt titel="Weitere Angaben" aufklappbar standardOffen={!!(formData.is_recurring || formData.contact_method || formData.task_type || formData.business_division || selectedDocuments.length)} testid="aufgaben-weitere-angaben">
              <FeldRaster className="pt-2">
                {!istStudent && (
                  <Feld label="Unternehmensbereich">
                    <select name="business_division" value={formData.business_division} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-business-division-select">
                      <option value="">Kein Bereich</option>
                      {businessDivisions.map((d) => <option key={d.id || d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </Feld>
                )}
                <Feld label="Aufgabentyp">
                  <select name="task_type" value={formData.task_type} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-type-select">
                    <option value="">Bitte wählen</option>
                    {AUFGABENTYPEN.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Feld>
                <Feld label="Geschätzte Stunden">
                  <input type="number" name="estimated_hours" value={formData.estimated_hours} onChange={handleChange} min="0" step="0.5" placeholder="0" className={`${EINGABE_KLASSE} w-24`} data-testid="task-hours-input" />
                </Feld>
                <Feld label="Erinnerung">
                  <span className="flex flex-wrap items-center gap-2">
                    <Switch checked={!!formData.reminder_enabled} onCheckedChange={(v) => setFeld('reminder_enabled', v)} data-testid="task-reminder-switch" />
                    {formData.reminder_enabled && (
                      <select name="reminder_time" value={formData.reminder_time} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-reminder-time-select">
                        {ERINNERUNG_OPTIONEN.map((o) => <option key={o.wert} value={o.wert}>{o.label}</option>)}
                      </select>
                    )}
                  </span>
                </Feld>
                <Feld label="Wiederholung" oben>
                  <div className="space-y-2" data-testid="recurring-section">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={formData.is_recurring} onChange={(e) => setFeld('is_recurring', e.target.checked)} className="w-4 h-4 rounded border-gray-300" data-testid="task-is-recurring" />
                      Wiederkehrende Aufgabe
                      <span className="text-xs text-gray-500">(beim Abschließen entsteht die nächste)</span>
                    </label>
                    {formData.is_recurring && (
                      <div className="flex flex-wrap items-center gap-2">
                        <select name="recurrence_pattern" value={formData.recurrence_pattern} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-recurrence-pattern">
                          {Object.entries(WIEDERHOLUNG_LABEL).map(([w, l]) => <option key={w} value={w}>{l}</option>)}
                        </select>
                        <span className="text-xs text-gray-500">alle</span>
                        <input type="number" name="recurrence_interval" value={formData.recurrence_interval} onChange={handleChange} min="1" max="365" className={`${EINGABE_KLASSE} w-16`} data-testid="task-recurrence-interval" />
                      </div>
                    )}
                    {formData.is_recurring && formData.recurrence_pattern === 'weekly' && (
                      <div className="flex flex-wrap gap-1" data-testid="task-recurrence-weekdays">
                        {WOCHENTAGE.map((lbl, idx) => {
                          const an = (formData.recurrence_days_of_week || []).includes(idx);
                          return (
                            <button
                              key={lbl}
                              type="button"
                              data-testid={`task-weekday-${idx}`}
                              onClick={() => setFormData((alt) => {
                                const s = new Set(alt.recurrence_days_of_week || []);
                                if (s.has(idx)) s.delete(idx); else s.add(idx);
                                return { ...alt, recurrence_days_of_week: Array.from(s).sort() };
                              })}
                              className={`w-8 h-8 rounded text-xs font-semibold ${an ? 'bg-[#2B2FB5] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                              {lbl}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Feld>
                <Feld label="Kontaktaufnahme" oben>
                  <div className="space-y-2" data-testid="contact-method-section">
                    <div className="flex flex-wrap items-center gap-2">
                      <select name="contact_method" value={formData.contact_method} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-contact-method-select">
                        <option value="">Keine</option>
                        {(contactMethods.length ? contactMethods.map((v) => v.label || v.value || v) : ['Anschreiben', 'E-Mail', 'Persönlich', 'WhatsApp'])
                          .map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                      {formData.contact_method === 'E-Mail' && (
                        <select name="contact_template_id" value={formData.contact_template_id} onChange={handleChange} className={EINGABE_KLASSE} data-testid="task-contact-template-select">
                          <option value="">Ohne Vorlage</option>
                          {emailTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      )}
                    </div>
                    {formData.contact_method === 'E-Mail' && formData.contact_template_id && (() => {
                      const tpl = emailTemplates.find((t) => t.id === formData.contact_template_id);
                      if (!tpl) return null;
                      return (
                        <div className="border border-gray-200 rounded-md overflow-hidden" data-testid="task-template-preview">
                          <div className="px-3 py-1.5 bg-gray-50 border-b text-xs"><b>Betreff:</b> {tpl.subject || '(kein Betreff)'}</div>
                          <div className="prose prose-sm max-w-none p-3 max-h-[200px] overflow-y-auto text-sm" dangerouslySetInnerHTML={{ __html: tpl.body_html || '<em>(kein Inhalt)</em>' }} data-testid="task-template-preview-body" />
                          <div className="px-3 py-1 bg-gray-50 border-t text-[11px] text-gray-500">Versand automatisch nach der Zeitplanung · Platzhalter: {'{vorname}'}, {'{nachname}'}, {'{firma}'}, {'{aufgabe_titel}'}</div>
                        </div>
                      );
                    })()}
                  </div>
                </Feld>
                <Feld label="Dokumente" oben>
                  <div className="space-y-2">
                    {selectedDocuments.length > 0 && (
                      <ul className="space-y-1">
                        {selectedDocuments.map((id) => (
                          <li key={id} className="flex items-center gap-2 text-sm">
                            <FileText className="w-3.5 h-3.5 text-gray-400" />{dokumentName(id)}
                            <button type="button" onClick={() => setSelectedDocuments((alt) => alt.filter((x) => x !== id))} className="text-gray-400 hover:text-red-500" aria-label="Dokument entfernen"><X className="w-3.5 h-3.5" /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <SuchAuswahl
                        platzhalter="Dokument verknüpfen …" suche={documentSearch} setSuche={setDocumentSearch} laedt={loadingDocuments}
                        eintraege={filteredDocuments} beschriftung={(d) => d.filename} unterzeile={(d) => d.description}
                        onWaehle={(d) => { setSelectedDocuments((alt) => [...alt, d.id]); setDocumentSearch(''); }}
                      />
                      <label className="inline-flex items-center gap-1.5 text-sm text-[#2B2FB5] cursor-pointer hover:underline">
                        {uploadingFile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Hochladen
                        <input type="file" multiple className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                      </label>
                    </div>
                  </div>
                </Feld>
              </FeldRaster>
              <div className="pt-3">
                <CustomFieldsEditor value={formData.custom_fields} onChange={(cf) => setFeld('custom_fields', cf)} />
              </div>
            </DetailAbschnitt>

            <DetailAbschnitt titel="KI-Unterstützung" aufklappbar standardOffen={false} testid="aufgaben-ki">
              <div className="space-y-4 pt-2">
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={handleAiAssist} disabled={aiLoading || !formData.title.trim()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#2B2FB5] text-white text-sm disabled:opacity-50" data-testid="ai-assist-btn">
                    {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {aiLoading ? 'Analysiere …' : 'Aufgabe optimieren'}
                  </button>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={includeWebSearch} onCheckedChange={setIncludeWebSearch} data-testid="task-ai-web-search" />
                    <Globe className="w-4 h-4 text-gray-500" />Internetrecherche einbeziehen
                  </label>
                </div>
                {aiResult && (
                  <div className="space-y-3">
                    {aiResult.refined_title && aiResult.refined_title !== formData.title && (
                      <div className="border border-gray-200 rounded-md p-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Optimierter Titel</span>
                          <button type="button" onClick={() => setFeld('title', aiResult.refined_title)} className="text-[#2B2FB5]" data-testid="apply-ai-title-btn">Übernehmen</button></div>
                        <p className="text-sm">{aiResult.refined_title}</p>
                      </div>
                    )}
                    {aiResult.refined_description && (
                      <div className="border border-gray-200 rounded-md p-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Konkretisierte Beschreibung</span>
                          <button type="button" onClick={() => setFeld('description', aiResult.refined_description)} className="text-[#2B2FB5]" data-testid="apply-ai-description-btn">Übernehmen</button></div>
                        <p className="text-sm whitespace-pre-line">{aiResult.refined_description}</p>
                      </div>
                    )}
                  </div>
                )}
                <FeldRaster>
                  <Feld label="Rolle">
                    <input type="text" name="role" value={formData.role} onChange={handleChange} placeholder="z. B. Marketing Manager" className={`${EINGABE_KLASSE} w-full max-w-[380px]`} data-testid="task-role-input" />
                  </Feld>
                  <Feld label="KI-Prompt" oben>
                    <div className="space-y-2">
                      <button type="button" onClick={generateAiPrompt} disabled={!formData.title.trim()} className="text-sm text-[#2B2FB5] hover:underline disabled:opacity-50" data-testid="generate-prompt-btn">Prompt aus Titel, Rolle und Beschreibung erzeugen</button>
                      {formData.ai_prompt && (
                        <>
                          <textarea name="ai_prompt" value={formData.ai_prompt} onChange={handleChange} rows={4} className={`${EINGABE_KLASSE} w-full font-mono`} data-testid="task-ai-prompt-input" />
                          <label className="flex items-center justify-between gap-2 text-sm"><span className="inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />Nach dem Speichern an KI-Assistent senden</span>
                            <Switch checked={formData.send_to_ai_assistant} onCheckedChange={(v) => setFeld('send_to_ai_assistant', v)} data-testid="send-to-assistant-switch" /></label>
                          <label className="flex items-center justify-between gap-2 text-sm"><span className="inline-flex items-center gap-1.5"><BookmarkPlus className="w-3.5 h-3.5" />In Promptsammlung speichern</span>
                            <Switch checked={formData.save_to_prompt_collection} onCheckedChange={(v) => setFeld('save_to_prompt_collection', v)} data-testid="save-to-collection-switch" /></label>
                          {formData.save_to_prompt_collection && promptCategories.length > 0 && (
                            <select value={selectedPromptCategory} onChange={(e) => setSelectedPromptCategory(e.target.value)} className={EINGABE_KLASSE}>
                              <option value="">Keine Kategorie</option>
                              {promptCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          )}
                        </>
                      )}
                    </div>
                  </Feld>
                </FeldRaster>
              </div>
            </DetailAbschnitt>
          </div>
        )}

        {/* ── Unteraufgaben ── */}
        {!istNeu && !bearbeiten && (
          <div className="mt-9">
            <DetailAbschnitt
              titel="Unteraufgaben"
              testid="aufgaben-unteraufgaben"
              rechts={unterBlaetter.length ? `${unterBlaetter.filter(istErledigt).length} von ${unterBlaetter.length} erledigt` : 'noch keine'}
            >
              <AufgabenBaum
                aufgaben={unteraufgaben}
                erledigteGetrennt={false}
                kompakt
                leerText=""
                onAuswahl={oeffne}
                onErledigt={(t, fertig) => statusSetzen(t, fertig ? 'completed' : 'open')}
              />
              {neueUnteraufgabe === null ? (
                !erledigt && (
                  <button type="button" onClick={() => setNeueUnteraufgabe('')} className="mt-1 w-full text-left pl-[47px] py-1.5 text-sm text-gray-500 hover:text-[#2B2FB5]" data-testid="aufgaben-unteraufgabe-neu">
                    + Unteraufgabe hinzufügen
                  </button>
                )
              ) : (
                <div className="pl-[47px] pr-2 py-1">
                  <input
                    autoFocus
                    value={neueUnteraufgabe}
                    onChange={(e) => setNeueUnteraufgabe(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); unteraufgabeAnlegen(); }
                      if (e.key === 'Escape') { e.preventDefault(); setNeueUnteraufgabe(null); }
                    }}
                    onBlur={() => { if (!neueUnteraufgabe?.trim()) setNeueUnteraufgabe(null); }}
                    placeholder="Titel der Unteraufgabe · Enter speichert, Esc bricht ab"
                    className={`${EINGABE_KLASSE} w-full`}
                    data-testid="aufgaben-unteraufgabe-eingabe"
                  />
                </div>
              )}
            </DetailAbschnitt>
          </div>
        )}

        {/* ── Gespeicherte KI-Empfehlung ── */}
        {!bearbeiten && aufgabe?.ai_solution_recommendation && (
          <div className="mt-8">
            <DetailAbschnitt
              titel="KI-Lösungsempfehlung"
              aufklappbar
              standardOffen={false}
              rechts={(
                <button type="button" onClick={() => { navigator.clipboard?.writeText(aufgabe.ai_solution_recommendation).then(() => toast.success('In Zwischenablage kopiert')).catch(() => {}); }} className="inline-flex items-center gap-1 text-[#2B2FB5]">
                  <Copy className="w-3 h-3" />Kopieren
                </button>
              )}
            >
              <p className="text-sm whitespace-pre-line leading-relaxed text-gray-700 flex gap-2"><Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />{aufgabe.ai_solution_recommendation}</p>
            </DetailAbschnitt>
          </div>
        )}

        {elternFuerNeu && <p className="mt-6 text-xs text-gray-500">Wird als Unteraufgabe von „{elternFuerNeu.title}“ angelegt.</p>}
      </div>

      {/* ── Fußleiste nur beim Bearbeiten ── */}
      {bearbeiten && (
        <div className="flex items-center justify-end gap-2 px-6 py-3 bg-white border-t border-gray-200 flex-shrink-0">
          <button type="button" onClick={abbrechen} disabled={speichert} className="px-4 py-1.5 rounded text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-60" data-testid="task-form-cancel">
            Abbrechen
          </button>
          <button type="submit" disabled={speichert} className="px-4 py-1.5 rounded text-sm font-semibold text-white bg-[#2B2FB5] hover:bg-[#23279a] flex items-center gap-2 disabled:opacity-60" data-testid="task-form-submit">
            {speichert && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {speichert ? 'Speichert …' : (istNeu ? 'Anlegen' : 'Speichern')}
          </button>
        </div>
      )}
    </form>
  );
}
